import React, { useMemo, useState } from 'react';
import type { FC } from 'react';
import { Alert, Input, Modal, Select, Spin } from 'antd';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'store';
import {
  generalSliceActions,
  generalSliceSelectors,
} from 'store/slices/general/config';
import { Form, Formik } from 'formik';
import { Button } from 'common/common_components/Button/Button';
import { contactUsScheme } from 'common/validation/schemes';
import type { ContactRequestBody } from 'store/slices/general/general.slice';
import { useTranslations } from 'hooks/useTranslations';
import { MailIcon } from 'icons/clientDashboard/Aside/links/MailIcon';
import styles from './ContactUsForm.module.scss';

interface ContactUsFormProps {
  btnClassName?: string;
  forceClassName?: boolean;
  label?: string;
}
export const ContactUsForm: FC<ContactUsFormProps> = ({
  btnClassName,
  forceClassName,
  label,
}) => {
  const supportsEmails = useAppSelector(
    generalSliceSelectors.selectSupportEmails,
  );
  const supportsEmailsIsPending = useAppSelector(
    generalSliceSelectors.selectSupportsEmailsPendingStatus,
  );
  const { t } = useTranslations();
  const dispatch = useAppDispatch();
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const [selectedRecipientEmail, setSelectedRecipientEmail] = useState('');
  const supportsEmailsOptions = useMemo(() => {
    return supportsEmails.map((item) => ({
      label: item,
      value: item,
    }));
  }, [supportsEmails]);
  const handleOpenModal = () => {
    setIsContactUsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsContactUsModalOpen(false);
  };

  const handleSelectRecipient = (val: string) => {
    setSelectedRecipientEmail(val);
  };

  const handleSubmit = (
    values: Omit<ContactRequestBody, 'recipientEmail'>,
    { resetForm }: { resetForm: () => void },
  ) => {
    dispatch(
      generalSliceActions.sendMailToSupport({
        ...values,
        recipientEmail: selectedRecipientEmail,
      }),
    );
    resetForm();
    setSelectedRecipientEmail('');
    handleCloseModal();
  };
  const className = forceClassName
    ? btnClassName
    : classNames('pr-5 text-sm cursor-pointer md:pr-1.5', btnClassName);
  return (
    <>
      <div onClick={handleOpenModal} className={className}>
        {supportsEmailsIsPending ? (
          <Spin spinning />
        ) : (
          <div className="flex items-center gap-2.5">
            <MailIcon />
            <span className="md:hidden">{label || t('CONTACT_US_BUTTON')}</span>
          </div>
        )}
      </div>
      <Modal
        className={styles.closeBtn}
        title="Contact us"
        open={isContactUsModalOpen}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Formik
          initialValues={{
            lastName: '',
            message: '',
            senderEmail: '',
            subject: '',
            firstName: '',
          }}
          validationSchema={contactUsScheme}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, handleChange, handleBlur }) => (
            <Form>
              <div className={styles.block}>
                <span className="!font-barlow">Recipient email (optional)</span>
                <Select
                  options={supportsEmailsOptions}
                  className="w-full"
                  placeholder="Please select recipient email"
                  value={selectedRecipientEmail}
                  onSelect={handleSelectRecipient}
                />
              </div>
              <div className="flex items-center gap-3">
                <div className={styles.block}>
                  <span className="!font-barlow">First name</span>
                  <Input
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Please write your first name"
                  />
                  {errors.firstName && touched.firstName && (
                    <Alert
                      type="error"
                      message={errors.firstName}
                      className="mt-3 !font-barlow"
                    />
                  )}
                </div>
                <div className={styles.block}>
                  <span className="!font-barlow">Last name</span>
                  <Input
                    value={values.lastName}
                    name="lastName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Please write your last name"
                  />
                  {errors.lastName && touched.lastName && (
                    <Alert
                      type="error"
                      message={errors.lastName}
                      className="mt-3 !font-barlow"
                    />
                  )}
                </div>
              </div>

              <div className={styles.block}>
                <span className="!font-barlow">Your email</span>
                <Input
                  value={values.senderEmail}
                  name="senderEmail"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Please write your email"
                />
                {errors.senderEmail && touched.senderEmail && (
                  <Alert
                    type="error"
                    message={errors.senderEmail}
                    className="mt-3 !font-barlow"
                  />
                )}
              </div>

              <div className={styles.block}>
                <span className="!font-barlow">Subject</span>
                <Input
                  value={values.subject}
                  name="subject"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Please write subject"
                />
                {errors.subject && touched.subject && (
                  <Alert
                    type="error"
                    message={errors.subject}
                    className="mt-3 !font-barlow"
                  />
                )}
              </div>

              <div className={styles.block}>
                <span className="!font-barlow">Message</span>
                <Input.TextArea
                  value={values.message}
                  name="message"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Please write your message"
                />
                {errors.message && touched.message && (
                  <Alert
                    type="error"
                    message={errors.message}
                    className="mt-3 !font-barlow"
                  />
                )}
              </div>

              <div className="w-full flex justify-end mt-8">
                <Button
                  content="Send"
                  type="submit"
                  cssType="dark"
                  className="!rounded-none"
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};
